import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listFolder } from '../api/files.ts';
import { useAuth } from '../store/auth.ts';
import { useUi } from '../store/ui.ts';
import { useSelection } from '../store/selection.ts';
import { useUploads } from '../store/uploads.ts';
import { useFolderActions } from '../hooks/useFolderActions.ts';
import { useContextMenu } from '../hooks/useContextMenu.ts';
import { useFileRowHandlers } from '../hooks/useFileRowHandlers.ts';
import { Breadcrumb } from '../components/files/Breadcrumb.tsx';
import { Toolbar } from '../components/files/Toolbar.tsx';
import { FileTable } from '../components/files/FileTable.tsx';
import { DropZone } from '../components/files/DropZone.tsx';
import { DetailsDrawer } from '../components/files/DetailsDrawer.tsx';
import { SelectionBar } from '../components/files/SelectionBar.tsx';
import { FileOverlays } from '../components/files/FileOverlays.tsx';
import { EmptyState } from '../components/common/EmptyState.tsx';
import { IconCloud } from '../lib/icons.tsx';
import type { ApiNode } from '../api/types.ts';

interface Props {
  /** 생략하면 개인 드라이브 */
  namespaceId?: string;
  /** 라우트 접두. 개인 드라이브는 '/files', 공유 폴더는 '/ns/<id>' */
  base?: string;
}

/**
 * 파일 목록 화면.
 *
 * 이 파일은 조립만 한다.
 *   useFileRowHandlers  행 조작(열기·선택·끌어놓기)
 *   SelectionBar        고른 항목에 대한 동작 줄
 *   FileOverlays        메뉴·미리보기·대화상자
 */
export function FilesPage({ namespaceId, base = '/files' }: Props) {
  const location = useLocation();
  const namespaces = useAuth((s) => s.namespaces);
  const sharingEnabled = useAuth((s) => s.features.sharing);
  const ns = namespaceId
    ? namespaces.find((n) => n.id === namespaceId)
    : namespaces.find((n) => n.type === 'personal') ?? namespaces[0];

  const path = decodeURIComponent(
    location.pathname.startsWith(base) ? location.pathname.slice(base.length) : '',
  ).replace(/\/$/, '');
  const sort = useUi((s) => s.sort);
  const order = useUi((s) => s.order);

  const selected = useSelection((s) => s.paths);
  const setSelection = useSelection((s) => s.set);
  const clearSelection = useSelection((s) => s.clear);

  const enqueueEntries = useUploads((s) => s.enqueueEntries);
  const actions = useFolderActions(ns?.id ?? '');
  const menu = useContextMenu();

  const [menuTarget, setMenuTarget] = useState<ApiNode | null>(null);
  const [renamingPath, setRenamingPath] = useState<string | null>(null);
  const [previewNode, setPreviewNode] = useState<ApiNode | null>(null);
  const [drawerNode, setDrawerNode] = useState<ApiNode | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<ApiNode | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['nodes', ns?.id, path, sort, order],
    queryFn: ({ signal }) => listFolder(ns!.id, path, sort, order, undefined, signal),
    enabled: Boolean(ns),
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const canEdit = ns?.role !== 'viewer';

  const handlers = useFileRowHandlers({
    items,
    base,
    actions,
    menu,
    onOpenPreview: setPreviewNode,
    onContextTarget: setMenuTarget,
    // 상세 패널이 이미 열려 있을 때만 선택을 따라간다
    // (클릭만으로 열리면 목록이 갑자기 좁아진다)
    onDetailsFollow: (node) => { setDrawerNode((prev) => (prev ? node : null)); },
    onRenameDone: () => { setRenamingPath(null); },
  });

  if (!ns) return <EmptyState title="드라이브를 찾을 수 없습니다" />;

  return (
    <div className="page">
      {/* 선택 중일 때는 같은 툴바 자리를 선택 동작이 차지한다 (SelectionBar 주석 참고) */}
      <div className="toolbar" data-mode={selected.length > 0 ? 'selection' : 'browse'}>
        {selected.length > 0 ? (
          <SelectionBar
            selected={selected}
            items={items}
            path={path}
            canEdit={canEdit}
            actions={actions}
            onClear={clearSelection}
          />
        ) : (
          <>
            <Breadcrumb
              base={base}
              crumbs={data?.breadcrumbs ?? [{ name: ns.name, path: '' }]}
              onDropTo={(target) => {
                if (selected.length > 0) {
                  actions.move.mutate({ paths: selected, targetFolder: target });
                }
              }}
            />
            <div className="spacer" />
            <Toolbar
              canEdit={canEdit}
              onNewFolder={() => { setNewFolderOpen(true); }}
              onUploadFiles={(files) => {
                enqueueEntries(
                  files.map((file) => ({ file, relativePath: file.name })), ns.id, path,
                );
              }}
              onUploadFolder={(files) => {
                enqueueEntries(
                  files.map((file) => ({
                    file,
                    relativePath: (file as File & { webkitRelativePath?: string })
                      .webkitRelativePath || file.name,
                  })),
                  ns.id, path,
                );
              }}
            />
          </>
        )}
      </div>

      <div className="page__body" style={{ display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {isLoading && <div className="empty"><span className="spinner" /></div>}
          {error && (
            <div className="empty"><p className="error-text">{(error as Error).message}</p></div>
          )}
          {!isLoading && !error && items.length === 0 && (
            <EmptyState
              icon={<IconCloud size={40} className="faint" />}
              title="폴더가 비어 있습니다"
              description="파일을 끌어다 놓거나 업로드 버튼을 눌러 보세요."
            />
          )}
          {!isLoading && items.length > 0 && (
            <FileTable
              namespaceId={ns.id}
              items={items}
              selected={selectedSet}
              renamingPath={renamingPath}
              onCancelRename={() => { setRenamingPath(null); }}
              onToggleAll={(checked) => {
                setSelection(checked ? items.map((i) => i.path) : []);
              }}
              handlers={handlers}
            />
          )}
        </div>
        {drawerNode && (
          <DetailsDrawer
            namespaceId={ns.id}
            node={drawerNode}
            onClose={() => { setDrawerNode(null); }}
          />
        )}
      </div>

      <DropZone
        disabled={!canEdit}
        onFiles={(entries) => { enqueueEntries(entries, ns.id, path); }}
      />

      <FileOverlays
        ns={ns}
        path={path}
        items={items}
        selected={selected}
        canEdit={canEdit}
        sharingEnabled={sharingEnabled}
        actions={actions}
        menu={menu}
        state={{ menuTarget, previewNode, shareTarget, newFolderOpen }}
        controls={{
          setMenuTarget, setPreviewNode, setDrawerNode, setShareTarget,
          setRenamingPath, setNewFolderOpen,
        }}
      />
    </div>
  );
}
